import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { Config } from '@root/modules/channel/types/channel';
import { GetArticleByID } from '@root/modules/article/types/article';
import getArticleByID from '@root/modules/article/graphql/getArticleByID.graphql';
import getArticleBodyByID from '@root/modules/article/graphql/getArticleBodyByID.graphql';

import getAccessVariables from '@root/modules/article/utils/getAccessVariables';

import { ArticleAdapter } from '../utils/ArticleAdapter';
import { dataLoader } from '@root/libs/redis/dataloader/dataLoader';

import { isValidSignedInt32 } from '@root/common/utils/helpers';

type ServiceVariables = {
  id: number | string;
  language?: string;
  preview?: string | null;
  zlickToken?: string | null;
  customerToken?: string | null;
  application?: Config['settings']['application'];
  settings?: {
    articleBaseDataExists: boolean;
    requestBody: boolean;
    authorizationHeader?: boolean;
  };
  getViews?: boolean;
};

type FetchVariables = GetArticleByID['variables'];

type Response = GetArticleByID['data'];

export default class ArticleService extends Service implements ServiceInterface {
  private getQueryWithOptions(serviceVariables: ServiceVariables) {
    const { id, preview, customerToken, zlickToken, application, settings, language, getViews } = serviceVariables;
    const variables: FetchVariables = { id: Number(id), authorLanguage: language, getViews };

    const { accessToken: articleToken, accessType } = getAccessVariables({ application, zlickToken: zlickToken || null, customerToken: customerToken || null });
    const query = settings?.articleBaseDataExists ? getArticleBodyByID : getArticleByID;

    const queryWithOptions = {
      query,
      options: {
        variables,
        context: { headers: {} as Record<string, unknown> },
      },
      articleToken,
    };

    if (preview) {
      queryWithOptions.options.context.headers.preview = preview;
      return queryWithOptions;
    }

    if (!articleToken) {
      return queryWithOptions;
    }

    switch (accessType) {
      case 'Customer':
        // Provide authorization header only on article body request
        if (settings?.authorizationHeader) {
          queryWithOptions.options.context.headers.Authorization = `Bearer ${articleToken}`;
        }
        break;
      case 'Zlick':
        queryWithOptions.options.context.headers['zlick-token'] = articleToken;
        break;
      default:
        break;
    }

    return queryWithOptions;
  }

  private async fetchArticle(variables: ServiceVariables, channelConfig: Config) {
    const { preview, application } = variables;

    if (isNaN(Number(variables.id)) || !isValidSignedInt32(Number(variables.id))) {
      this.handleError({ message: 'article.error.not_found', statusCode: 400, extraData: 'invalid input `id`' });
      return;
    }

    // Set getViews for Video channel
    if (channelConfig.settings.id === 'video') {
      variables.getViews = true;
    }

    const { query, options: queryOptions, articleToken } = this.getQueryWithOptions(variables);
    const cacheData = !preview && !application?.isSearchBot;
    try {
      const options = Object.assign({ query }, queryOptions);
      const dataLoaderOptions = {
        remote: {
          keyPrefix: 'article',
        },
      };
      const requestWrapper = (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });
        return apiProvider.request<Response>(options);
      };

      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader && cacheData ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response?.article?.data.length) {
        this.handleError({ message: 'article.error.not_found', statusCode: 404 });
        return;
      }

      const articleAdapter = new ArticleAdapter(response.article.data[0], channelConfig.page.article.component);
      const article = articleAdapter.adapt();

      return {
        article,
        access: Boolean(article.content?.paywall?.access),
        articleToken,
      };
    } catch (e) {
      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: { variables: ServiceVariables; channelConfig: Config }) {
    const isHealthy = this.getHealthCheckStatus('content-api-v3');

    if (!isHealthy) {
      this.handleError({ statusCode: 500, message: 'error.unexpected_system_error' });
      return;
    }

    const response = await this.fetchArticle(variables.variables, variables.channelConfig);

    return response;
  }
}

export function fetchArticle(variables: ServiceVariables, { req, channelConfig }: { req: PortalRootIncomingMessage; channelConfig: Config }) {
  const articleService = new ArticleService({ req });
  return articleService.fetch({ variables, channelConfig });
}
